<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router/composables"

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue"
import OrganizationOrderView from "@Modules/shoppingcart/Views/OrganizationOrderView.vue"
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization"
import { XOrdersStore } from "@Modules/shoppingcart/Store/XOrdersStore"
import { XAuthStore } from "@Modules/users/Store/XAuthStore"
import useNotify from "@Composables/useNotify"
import useTranslation from "@Composables/useTranslation"

//
// Setup
const route = useRoute()
const router = useRouter()
const { currentOrganizationSlug, currentOrganization } = useCurrentOrganization()
const ordersStore = XOrdersStore()
const authStore = XAuthStore()
const { notifyError, notifyInfo } = useNotify()
const { __ } = useTranslation()

//
// Data
const loading = ref(false)

//
// Computed
const orderIdNum = computed(() => Number(route.params.orderId))
const locale = computed(() => authStore.locale)

const order = computed(() => {
  return ordersStore.getOrderById(orderIdNum.value) ?? {}
})

const orderFailed = computed(() => order.value.isFailed?.() ?? false)

const total = computed(() => order.value.amount?.() ?? order.value.total ?? null)

const translatedTotalQuantity = computed(() => {
  const totalQuantity = order.value.totalQuantity ?? 0
  const quantity = order.value.quantity ?? 0
  const translatedItem = __("item", { count: totalQuantity })
  return `${quantity} of ${totalQuantity} ${translatedItem}`
})

const pdfUrl = computed(() => {
  if (!currentOrganizationSlug.value) return ""
  return ordersStore.getOrderPdfUrlForOrganization(currentOrganizationSlug.value, orderIdNum.value)
})

//
// Methods
function goBack(): void {
  if (!currentOrganizationSlug.value) return
  router.push({ name: "org.shopping-cart.orders", params: { org: currentOrganizationSlug.value } })
}

async function recoverNow(): Promise<void> {
  const result = await ordersStore.retryCheckout(orderIdNum.value)
  if (result) {
    notifyInfo({ text: "Checkout retry initiated" })
  } else {
    notifyError({ text: "Failed to retry checkout" })
  }
}

async function loadData(): Promise<void> {
  if (!currentOrganizationSlug.value) return

  loading.value = true

  const fetchedOrder = await ordersStore.fetchOrderForOrganization(
    currentOrganizationSlug.value,
    orderIdNum.value,
    { disableErrorInterceptors: true },
  )

  loading.value = false

  if (!fetchedOrder && currentOrganizationSlug.value) {
    const errorMessage = ordersStore.errors.message
    notifyError({ text: errorMessage || "Something went wrong" })
    router.push({ name: "org.shopping-cart.orders", params: { org: currentOrganizationSlug.value } })
  }
}

//
// Lifecycle
onMounted(() => {
  loadData()
})

//
// Exposed Test Vars
defineExpose({ loading, order, currentOrganizationSlug, orderIdNum, currentOrganization })
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization"
    :organization="currentOrganization"
    :can="currentOrganization?.can('manage_subscriptions') || false"
  >
    <OrganizationOrderView
      :order="order"
      :order-id="orderIdNum"
      :loading="loading"
      :locale="locale"
      :order-failed="orderFailed"
      :total="total"
      :translated-total-quantity="translatedTotalQuantity"
      :pdf-url="pdfUrl"
      :go-back="goBack"
      :recover-now="recoverNow"
    />
  </OrgSettingsLayout>
</template>
